import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CreditCard, Home as HomeIcon, Car, Store, Calculator, ArrowRight, Info, Shield, Clock, Award, ChevronDown, Star, Zap, Pencil, Check } from 'lucide-react';
import FAQ from '../components/FAQ';
import SponsoredBanks from '../components/SponsoredBanks';
import CostTable from '../components/CostTable';
import BankInterestRates from '../components/BankInterestRates';
import LoanOnboarding from '../components/LoanOnboarding';
import AdContainer from '../components/AdContainer';
import { loanTypes } from '../data/loanData';

// Define bank data with page-specific ordering
const banks = [
  {
    id: 'akbank',
    name: 'Akbank',
    logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/a80c0972-6830-4dc4-b3bc-e236c7fbb5ea.png',
    pageOrders: [
      { page: 'ihtiyac-kredisi', displayOrder: 1 },
      { page: 'konut-kredisi', displayOrder: 2 },
      { page: 'tasit-kredisi', displayOrder: 1 },
      { page: 'kobi-kredisi', displayOrder: 3 }
    ]
  },
  {
    id: 'garanti',
    name: 'Garanti BBVA',
    logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/2cf1c9fe-fbff-4874-a365-fd63455d078c.png',
    pageOrders: [
      { page: 'ihtiyac-kredisi', displayOrder: 2 },
      { page: 'konut-kredisi', displayOrder: 1 },
      { page: 'tasit-kredisi', displayOrder: 2 },
      { page: 'kobi-kredisi', displayOrder: 1 }
    ]
  },
  {
    id: 'yapikredi',
    name: 'Yapı Kredi',
    logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/52f6626d-498d-4e76-8fb5-fd4381a1e0db.png',
    pageOrders: [
      { page: 'ihtiyac-kredisi', displayOrder: 3 },
      { page: 'konut-kredisi', displayOrder: 3 },
      { page: 'tasit-kredisi', displayOrder: 3 },
      { page: 'kobi-kredisi', displayOrder: 2 }
    ]
  }
];

const IhtiyacKredisi = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [amount, setAmount] = useState(10000);
  const [isManualInput, setIsManualInput] = useState(false);
  const [manualAmount, setManualAmount] = useState('');
  const [term, setTerm] = useState(12);
  const [isOnboardingOpen, setIsOnboardingOpen] = useState(false);
  const currentPage = location.pathname.substring(1); // Remove leading slash

  // Determine selected loan type from URL
  const selectedLoan = loanTypes.find(loan => loan.path === location.pathname) || loanTypes[0];

  // Helper function to get max term for a given amount
  const getMaxTerm = (loanType: any, amount: number) => {
    if (loanType.getMaxTerm && typeof loanType.getMaxTerm === 'function') {
      return loanType.getMaxTerm(amount);
    }
    // Fallback to maxTerm property or default value
    return loanType.maxTerm || 60;
  };

  // Helper function to get available terms for a given amount
  const getAvailableTerms = () => {
    if (selectedLoan.id === 'ihtiyac') {
      if (amount < 125000) return selectedLoan.terms.low;
      if (amount <= 250000) return selectedLoan.terms.medium;
      return selectedLoan.terms.high;
    } else if (selectedLoan.id === 'tasit') {
      if (amount <= 2500000) return selectedLoan.terms.level1;
      if (amount <= 5000000) return selectedLoan.terms.level2;
      if (amount <= 6500000) return selectedLoan.terms.level3;
      if (amount <= 7500000) return selectedLoan.terms.level4;
      return selectedLoan.terms.level5;
    } else {
      // For konut and other loan types
      return [
        selectedLoan.minTerm || 12,
        selectedLoan.minTerm ? selectedLoan.minTerm + Math.round((selectedLoan.maxTerm - selectedLoan.minTerm) * 0.33) : 24,
        selectedLoan.minTerm ? selectedLoan.minTerm + Math.round((selectedLoan.maxTerm - selectedLoan.minTerm) * 0.66) : 36,
        selectedLoan.maxTerm || 48
      ];
    }
  };

  // Update amount and term when loan type changes
  useEffect(() => {
    setAmount(selectedLoan.minAmount);
    const maxTerm = getMaxTerm(selectedLoan, selectedLoan.minAmount);
    setTerm(Math.min(term, maxTerm));
  }, [selectedLoan.id]);

  useEffect(() => {
    // Update term if current term is greater than max allowed for new amount
    const maxTerm = getMaxTerm(selectedLoan, amount);
    if (term > maxTerm) {
      setTerm(maxTerm);
    }
  }, [amount, selectedLoan.id]);

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Math.round(parseInt(e.target.value.replace(/\D/g, '')) / selectedLoan.step) * selectedLoan.step;
    if (value >= selectedLoan.minAmount && value <= selectedLoan.maxAmount) {
      setAmount(value);
      
      // Update term if current term is greater than max allowed for new amount
      const maxTerm = getMaxTerm(selectedLoan, value);
      if (term > maxTerm) {
        setTerm(maxTerm);
      }
    }
  };

  const formatNumber = (value: string) => {
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  };

  const handleManualAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/\D/g, '');
    const formattedValue = formatNumber(rawValue);
    setManualAmount(formattedValue);
    
    const numValue = parseInt(rawValue);
    if (!isNaN(numValue) && numValue >= selectedLoan.minAmount && numValue <= selectedLoan.maxAmount) {
      // Round to nearest step value
      const roundedValue = Math.round(numValue / selectedLoan.step) * selectedLoan.step;
      setAmount(roundedValue);
    }
  };

  const handleConfirmManualInput = () => {
    const numValue = parseInt(manualAmount.replace(/\./g, ''));
    if (!isNaN(numValue) && numValue >= selectedLoan.minAmount && numValue <= selectedLoan.maxAmount) {
      // Round to nearest step value
      const roundedValue = Math.round(numValue / selectedLoan.step) * selectedLoan.step;
      setAmount(roundedValue);
      setIsManualInput(false);
    }
  };

  const handleCalculate = () => {
    setIsOnboardingOpen(true);
  };

  const availableTerms = getAvailableTerms();
  const maxTerm = getMaxTerm(selectedLoan, amount);

  // Get banks sorted by the current page's display order
  const getSortedBanksForPage = () => {
    return [...banks].sort((a, b) => {
      const aOrder = a.pageOrders.find(po => po.page === currentPage)?.displayOrder || 999;
      const bOrder = b.pageOrders.find(po => po.page === currentPage)?.displayOrder || 999;
      return aOrder - bOrder;
    });
  };

  const sortedBanks = getSortedBanksForPage();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Masthead Ad */}
      <AdContainer location="masthead" />

      {/* Hero Section with Calculator */}
      <div className="bg-gradient-to-b from-[#F7BA45]/20 to-slate-50 py-8 md:py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-800 mb-4">
              {selectedLoan.title}
            </h1>
            <p className="text-base md:text-lg text-slate-600 mb-8 md:mb-12">
              {selectedLoan.description}
            </p>

            {/* Loan Type Selection */}
            <div className="bg-white rounded-xl md:rounded-2xl p-2 shadow-lg border border-slate-200 flex flex-nowrap overflow-x-auto scrollbar-thin gap-2 mb-8">
              {loanTypes.map((type) => (
                <button
                  key={type.id}
                  onClick={() => navigate(type.path)}
                  className={`flex items-center gap-2 md:gap-3 px-4 md:px-6 py-3 md:py-4 rounded-lg md:rounded-xl transition-all duration-300 relative flex-shrink-0
                    ${type.path === location.pathname
                      ? 'bg-[#F7BA45] text-slate-900 shadow-md scale-105'
                      : 'hover:bg-slate-100 text-slate-600'
                    }`}
                >
                  <div className={`transition-colors duration-300
                    ${type.path === location.pathname ? 'text-slate-900' : 'text-slate-400'}`}>
                    {type.icon}
                  </div>
                  <span className="font-medium whitespace-nowrap text-sm md:text-base">{type.title}</span>
                </button>
              ))}
            </div>

            {/* Calculator */}
            <div className="bg-white rounded-xl md:rounded-2xl p-4 md:p-8 shadow-lg border border-slate-200">
              <div className="grid md:grid-cols-2 gap-4 md:gap-8 mb-6 md:mb-8">
                {/* Amount Input */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-slate-700">
                      Kredi Tutarı
                    </label>
                    <div>
                      {isManualInput ? (
                        <div className="flex items-center bg-[#F7BA45]/5 rounded-lg px-3 py-1">
                          <input
                            type="text"
                            value={manualAmount}
                            onChange={handleManualAmountChange}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleConfirmManualInput();
                              }
                            }}
                            onBlur={handleConfirmManualInput}
                            className="w-32 text-right bg-transparent border-none focus:outline-none text-lg font-semibold text-slate-800"
                            autoFocus
                          />
                          <span className="ml-1 text-lg font-semibold text-slate-800">TL</span>
                        </div>
                      ) : (
                        <button
                          onClick={() => {
                            setIsManualInput(true);
                            setManualAmount(formatNumber(amount.toString()));
                          }}
                          className="text-lg font-semibold text-slate-800 hover:bg-[#F7BA45]/5 px-3 py-1 rounded-lg transition-colors"
                        >
                          {formatNumber(amount.toString())} TL
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="space-y-4">
                    <input
                      type="range"
                      min={selectedLoan.minAmount}
                      max={selectedLoan.maxAmount}
                      step={selectedLoan.step}
                      value={amount}
                      onChange={handleAmountChange}
                      className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer"
                    />
                    
                    <div className="grid grid-cols-5 gap-2">
                      {selectedLoan.amounts.map((point) => (
                        <button
                          key={point}
                          onClick={() => setAmount(point)}
                          className={`py-2 px-3 text-center text-sm rounded transition-all ${
                            amount === point
                              ? 'bg-[#F7BA45] text-white shadow-md'
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                        >
                          {formatNumber(point.toString())}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Term Input */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-slate-700">
                      Vade
                    </label>
                    <span className="text-lg font-semibold text-slate-800">
                      {term} Ay
                    </span>
                  </div>

                  <div className="space-y-4">
                    <div className="grid grid-cols-6 gap-2">
                      {availableTerms.map((t) => (
                        <button
                          key={t}
                          onClick={() => setTerm(t)}
                          className={`py-2 px-3 text-center rounded transition-all ${
                            term === t
                              ? 'bg-[#F7BA45] text-white shadow-md'
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                        >
                          {t}
                        </button>
                      ))}
                    </div>

                    {/* Term Info */}
                    <div className="bg-[#F7BA45]/5 rounded-lg p-3 border border-[#F7BA45]/10">
                      <div className="flex items-center gap-2 text-sm text-slate-700">
                        <Info size={16} className="text-[#F7BA45]" />
                        <span>
                          Seçtiğiniz kredi tutarı için maksimum vade {maxTerm} aydır
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Calculate Button */}
              <button
                onClick={handleCalculate}
                className="w-full bg-[#F7BA45] hover:bg-[#e5ac3d] text-slate-900 font-medium py-4 rounded-lg transition-all duration-300 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl"
              >
                KREDİ HESAPLA
                <ArrowRight size={20} />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 md:py-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Left Sidebar Ad */}
            <AdContainer location="sidebar-left" className="order-2 lg:order-1" />

            {/* Main Content */}
            <div className="flex-1 order-1 lg:order-2">
              {/* Sponsored Banks Section */}
              <div className="mb-8">
                <div className="flex items-center gap-2 text-[#F7BA45] mb-4 md:mb-6">
                  <Star className="h-5 w-5 md:h-6 md:w-6" />
                  <h2 className="text-xl md:text-2xl font-bold text-slate-800">
                    Öne Çıkan Kredi Fırsatları
                  </h2>
                </div>
                <SponsoredBanks />
              </div>

              {/* Tables Section */}
              <div className="space-y-8 md:space-y-12">
                {/* Cost Table */}
                <div>
                  <CostTable />
                </div>

                {/* Bank Interest Rates */}
                <div>
                  <BankInterestRates />
                </div>
              </div>
            </div>

            {/* Right Sidebar Ad */}
            <AdContainer location="sidebar-right" className="order-3" />
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="bg-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-8 md:mb-12">
              <h2 className="text-2xl md:text-3xl font-bold mb-3 md:mb-4 text-slate-800">
                Sık Sorulan Sorular
              </h2>
              <p className="text-base md:text-lg text-slate-600 mb-8 md:mb-12">
                {selectedLoan.title} başvurusu ve kullanımı hakkında merak ettiğiniz tüm sorular
              </p>
            </div>
            <FAQ />
          </div>
        </div>
      </div>

      {/* Loan Onboarding Modal */}
      <LoanOnboarding
        isOpen={isOnboardingOpen}
        onClose={() => setIsOnboardingOpen(false)}
        loanAmount={amount}
        loanTerm={term}
      />
    </div>
  );
};

export default IhtiyacKredisi;